import React from 'react';
import { AvailabilityWindow } from './types';

interface AvailabilityOverlayProps {
  availabilityWindow: AvailabilityWindow;
  selectedDate: Date;
  hourWidth: number;
  totalRows: number;
  rowHeight: number;
  startDate?: Date; // For continuous timeline
}

export function AvailabilityOverlay({
  availabilityWindow,
  selectedDate,
  hourWidth,
  totalRows,
  rowHeight,
  startDate
}: AvailabilityOverlayProps) {
  // Use startDate if provided (continuous timeline), otherwise use selectedDate (day start)
  const dayStart = startDate || (() => {
    const d = new Date(selectedDate);
    d.setHours(0, 0, 0, 0);
    return d;
  })();
  
  // Calculate positions
  const startHours = (availabilityWindow.startTime.getTime() - dayStart.getTime()) / (1000 * 60 * 60);
  const endHours = (availabilityWindow.endTime.getTime() - dayStart.getTime()) / (1000 * 60 * 60);
  
  const left = startHours * hourWidth;
  const width = (endHours - startHours) * hourWidth;
  const height = totalRows * rowHeight;
  const headerHeight = 64; // Timeline header height (h-16)
  const totalHeight = height + headerHeight;
  
  const formatTime = (date: Date) => {
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${minutes.toString().padStart(2, '0')} ${ampm}`;
  };
  
  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };
  
  const duration = Math.round((availabilityWindow.endTime.getTime() - availabilityWindow.startTime.getTime()) / (1000 * 60));
  const durationText = duration >= 60 
    ? `${Math.floor(duration / 60)}h ${duration % 60 > 0 ? duration % 60 + 'm' : ''}`
    : `${duration}m`;
  
  return (
    <>
      {/* Start time vertical bar (Pickup) - Semi-transparent */}
      <div 
        className="absolute pointer-events-none z-30" 
        style={{ left, top: -headerHeight, height: totalHeight }}
      >
        <div className="absolute inset-y-0 left-0 w-1 bg-green-600 opacity-40" />
      </div>
      
      {/* End time vertical bar (Drop) - Semi-transparent */}
      <div 
        className="absolute pointer-events-none z-30" 
        style={{ left: left + width, top: -headerHeight, height: totalHeight }}
      >
        <div className="absolute inset-y-0 left-0 w-1 bg-red-600 opacity-40" />
      </div>
      
      {/* Shaded region between start and end - Very subtle */}
      <div 
        className="absolute pointer-events-none z-[25]" 
        style={{ left, width, top: -headerHeight, height: totalHeight }}
      >
        <div className="absolute inset-0 bg-blue-400 opacity-15" />
      </div>
    </>
  );
}